import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { useCompanies } from "../hooks/useCompanies";
import { useTranslation } from "react-i18next";

const CompanySelector = ({
  placeholder,
  maxDisplayNames = 2,
}) => {
  const { t } = useTranslation("shell");
  const theme = useTheme();
  const selectPh = placeholder ?? t("company.selectPlaceholder");

  const [anchorEl, setAnchorEl] = useState(null);

  // Persistido (escopo atual da aplicação)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const saved = sessionStorage.getItem("selectedCompanies");
    return saved ? JSON.parse(saved) : [];
  });

  // Draft (edição local)
  const [draftCompanyIds, setDraftCompanyIds] = useState([]);

  const [hasChanges, setHasChanges] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [filterText, setFilterText] = useState("");

  const { data, isLoading, isError, refetch } = useCompanies(
    1,
    100,
    filterText
  );

  const companies = useMemo(
    () => data?.companies || [],
    [data?.companies]
  );

  // IDs de todas carregadas
  const allCompanyIds = useMemo(
    () => companies.map((c) => c.id),
    [companies]
  );

  // Estados do checkbox mestre (baseado no draft)
  const allSelected = useMemo(
    () =>
      allCompanyIds.length > 0 &&
      draftCompanyIds.length === allCompanyIds.length,
    [allCompanyIds, draftCompanyIds]
  );

  const someSelected = useMemo(
    () =>
      draftCompanyIds.length > 0 &&
      draftCompanyIds.length < allCompanyIds.length,
    [allCompanyIds, draftCompanyIds]
  );

  // Selecionar todas na primeira carga se não houver sessão
  useEffect(() => {
    if (!selectedCompanyIds.length && companies.length) {
      setSelectedCompanyIds(allCompanyIds);
      sessionStorage.setItem(
        "selectedCompanies",
        JSON.stringify(allCompanyIds)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  // ===== MENU =====

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setDraftCompanyIds(selectedCompanyIds);
    setHasChanges(false);
  };

  const handleCloseMenu = () => {
    if (hasChanges) {
      setShowWarning(true);
    }
    setAnchorEl(null);
  };

  // ===== TOGGLES =====

  const handleToggleCompany = (companyId) => {
    setDraftCompanyIds((prev) => {
      const updated = prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId];

      setHasChanges(true);
      return updated;
    });
  };

  const handleToggleAll = () => {
    setDraftCompanyIds((prev) => {
      const updated =
        prev.length === allCompanyIds.length
          ? []
          : allCompanyIds;

      setHasChanges(true);
      return updated;
    });
  };

  // ===== APLICAR =====

  const handleApplyChanges = () => {
    sessionStorage.setItem(
      "selectedCompanies",
      JSON.stringify(draftCompanyIds)
    );

    setSelectedCompanyIds(draftCompanyIds);
    setHasChanges(false);

    window.location.reload();
  };

  // ===== FILTRO =====

  const handleFilterChange = (event) =>
    setFilterText(event.target.value);

  const handleKeyDown = (event) => {
    if (["ArrowUp", "ArrowDown", "Enter"].includes(event.key))
      return;
    event.stopPropagation();
  };

  // ===== TEXTO DO BOTÃO =====

  const displayText = () => {
    if (!selectedCompanyIds.length) return placeholder;

    const selectedCompanyNames = companies
      .filter((company) =>
        selectedCompanyIds.includes(company.id)
      )
      .map((company) => company.nome);

    if (selectedCompanyNames.length <= maxDisplayNames) {
      return selectedCompanyNames.join(", ");
    }

    const displayed = selectedCompanyNames
      .slice(0, maxDisplayNames)
      .join(", ");

    const remainingCount =
      selectedCompanyNames.length - maxDisplayNames;

    return `${displayed} +${remainingCount}`;
  };

  // ===== RENDER =====

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Button
          variant="outlined"
          onClick={handleOpenMenu}
          endIcon={<ArrowDropDown />}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            p: "6px 12px",
            fontSize: "14px",
            width: "100%",
            maxWidth: "300px",
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {displayText()}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            style: {
              maxHeight: 360,
              width: 280,
              backgroundColor:
                theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          }}
        >
          {/* Filtro */}
          <Box sx={{ p: "8px 12px" }}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={t("company.filterPlaceholder")}
              value={filterText}
              onChange={handleFilterChange}
              onKeyDown={handleKeyDown}
            />
          </Box>

          {/* Selecionar todas */}
          <MenuItem onClick={handleToggleAll}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
            />
            <ListItemText
              primary={t("company.allCompanies")}
              secondary={
                companies.length
                  ? t("company.selectedCount", {
                      current: draftCompanyIds.length,
                      total: companies.length,
                    })
                  : undefined
              }
            />
          </MenuItem>

          <Divider />

          {/* Loading */}
          {isLoading && (
            <MenuItem disabled>
              <CircularProgress size={24} />
            </MenuItem>
          )}

          {/* Error */}
          {isError && (
            <MenuItem onClick={refetch}>
              Erro ao carregar empresas.
            </MenuItem>
          )}

          {/* Lista */}
          {!isLoading &&
            companies.map((company) => (
              <MenuItem
                key={company.id}
                onClick={() =>
                  handleToggleCompany(company.id)
                }
              >
                <Checkbox
                  checked={draftCompanyIds.includes(
                    company.id
                  )}
                />
                <ListItemText
                  primary={company.nome}
                />
              </MenuItem>
            ))}

          {/* Footer fixo */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              p: "8px 12px",
              backgroundColor:
                theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {hasChanges && (
              <Button
                variant="contained"
                onClick={handleApplyChanges}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: "16px",
                  fontSize: "14px",
                }}
              >
                {t("company.applyChanges")}
              </Button>
            )}
          </Box>
        </Menu>
      </Box>

      {/* Aviso ao fechar sem aplicar */}
      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={() => setShowWarning(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setShowWarning(false)}
        >
          {t("company.unsavedChanges")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CompanySelector;
